import type { AuditEntry, CreateUserBody, UpdateUserBody, User } from '@tasks/contracts';

import type { Page } from '../lib/types';
import { apiRequest } from './client';

export type ListUsersParams = {
  readonly q?: string;
  readonly status?: 'active' | 'inactive' | 'all';
  readonly limit?: number;
  readonly offset?: number;
};

export type AuditParams = {
  readonly actorId?: string;
  readonly action?: string;
  readonly from?: string;
  readonly to?: string;
  readonly limit?: number;
  readonly offset?: number;
};

export function listUsers(params: ListUsersParams = {}, signal?: AbortSignal): Promise<Page<User>> {
  return apiRequest<Page<User>>('/admin/users', {
    query: { q: params.q, status: params.status, limit: params.limit, offset: params.offset },
    signal,
  });
}

export function getUser(id: string, signal?: AbortSignal): Promise<User> {
  return apiRequest<User>(`/admin/users/${encodeURIComponent(id)}`, { signal });
}

export function createUser(body: CreateUserBody): Promise<User> {
  return apiRequest<User>('/admin/users', { method: 'POST', body });
}

export function updateUser(id: string, body: UpdateUserBody): Promise<User> {
  return apiRequest<User>(`/admin/users/${encodeURIComponent(id)}`, { method: 'PATCH', body });
}

/** Аватар другого пользователя или агента меняет только системный админ. */
export function uploadUserAvatar(id: string, file: File): Promise<User> {
  const formData = new FormData();
  formData.append('file', file);
  return apiRequest<User>(`/admin/users/${encodeURIComponent(id)}/avatar`, {
    method: 'POST',
    formData,
  });
}

export function removeUserAvatar(id: string): Promise<User> {
  return apiRequest<User>(`/admin/users/${encodeURIComponent(id)}/avatar`, { method: 'DELETE' });
}

/** Деактивация/активация — это PATCH с флагом (docs/05 §3, UC-USR-03). */
export function setUserActive(id: string, isActive: boolean): Promise<User> {
  return updateUser(id, { isActive });
}

export function listAuditEntries(params: AuditParams = {}, signal?: AbortSignal): Promise<Page<AuditEntry>> {
  return apiRequest<Page<AuditEntry>>('/admin/audit', {
    query: {
      actorId: params.actorId,
      action: params.action,
      from: params.from,
      to: params.to,
      limit: params.limit,
      offset: params.offset,
    },
    signal,
  });
}
